import { useState } from 'react';
import { claimLearnerByLinkCode } from '../../../data/supabase/authService';
import { getActiveProfile } from '../../../data/supabase/session';
import { syncLinkedChildren } from '../../../data/supabase/syncLinkedChildren';
interface UseLinkChildReturn {
  /** Current value of the koppelcode input. */
  linkCode: string;
  setLinkCode: (code: string) => void;
  /** Harde fout: lege of ongeldige code, te veel pogingen, maximum bereikt. */
  error: string;
  /** Bevestiging na een geslaagde koppeling. Noemt het kind bij naam. */
  success: string;
  /**
   * Aanvullende mededeling bij een geslaagde koppeling die de ouder moet
   * lezen maar die de koppeling niet tegenhoudt — nu: er kijkt al iemand
   * anders mee. Bewust gescheiden van `success`, zodat de UI hem anders kan
   * tonen dan een groen vinkje.
   */
  notice: string;
  /** True zolang de server bezig is. Voorkomt dubbel indienen. */
  isSubmitting: boolean;
  handleSubmit: (e: React.FormEvent) => void | Promise<void>;
}

/**
 * Hook voor het koppelen van een ouder aan een kind via de koppelcode.
 *
 * ── Waarom er GEEN bevestigingsstap meer is ───────────────────────────────
 *
 * Hier stond een tweetrapsflow: code invoeren → kind tonen → bevestigen, met
 * een `multi_parent`-waarschuwing vóór de bevestiging. Die flow werkte op de
 * lokale accountlijst, en draaide sinds de omzetting naar Supabase niet meer:
 * `claim_learner_by_link_code` koppelt METEEN, dus `pendingLearner` werd nooit
 * meer gevuld en het bevestigingspaneel rende nooit. Dood, maar onzichtbaar
 * dood — de code stond er nog compleet bij.
 *
 * Die stap terugbouwen kan niet, en dat is geen tekortkoming van de RPC maar
 * van de vraag: vóór de koppeling mag de ouder de profielrij van het kind niet
 * lezen. Er valt dus letterlijk niets te tonen om te bevestigen — de oude
 * flow toonde een naam die op het apparaat van de ouder nooit had kunnen
 * staan. Een bevestigingsscherm zonder inhoud is theater.
 *
 * Wat wél kan, en sinds 0029 gebeurt: de server vertelt achteraf WAT er
 * gekoppeld is (naam) en of er al iemand anders meekijkt. De waarschuwing komt
 * daarmee ná de koppeling in plaats van ervoor — minder ideaal, maar wel écht,
 * en de ouder kan er nog steeds naar handelen (ontkoppelen kan met één knop).
 *
 * Het maximum van vijf ouders is meeverhuisd naar de database (0029). Dat
 * stond alleen in deze client, in de tak die niet meer draaide — het werd dus
 * nergens meer afgedwongen.
 */
export function useLinkChild(): UseLinkChildReturn {
  const [linkCode, setLinkCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [notice, setNotice] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    setError('');
    setSuccess('');
    setNotice('');
    const code = linkCode.trim().toUpperCase();
    if (!code) {
      setError('Vul een koppelcode in.');
      return;
    }
    setIsSubmitting(true);
    try {
      // De ouder zit op zijn EIGEN apparaat en heeft het account van het kind
      // dus niet lokaal staan. Zoeken in de lokale accountlijst vond daarom
      // nooit iets — de koppeling kon alleen slagen als ouder en kind
      // toevallig dezelfde browser deelden.
      const result = await claimLearnerByLinkCode(code);
      if (!result.ok || !result.learnerId) {
        setError(result.message ?? 'Geen leerling gevonden met deze koppelcode. Controleer de code en probeer opnieuw.');
        return;
      }
      const name = result.learnerName?.trim();

      // Al gekoppeld is geen fout en geen nieuwe koppeling. Het als succes
      // vieren zou de ouder laten denken dat er iets veranderd is; het als
      // fout tonen zou hem laten zoeken naar een probleem dat er niet is.
      if (result.alreadyLinked) {
        setSuccess(name ? `${name} was al aan jouw account gekoppeld. Er is niets veranderd.` : 'Dit kind was al aan jouw account gekoppeld. Er is niets veranderd.');
        setLinkCode('');
        return;
      }
      setSuccess(name ? `${name} is gekoppeld. Je ziet de voortgang terug in je overzicht.` : 'Gekoppeld!');

      /*
       De koppeling staat nu op de server, maar de kinderlijst en de
       kindwisselaar lezen de LOKALE accountstaat. Die werd alleen bij het
       inloggen bijgewerkt — en koppelen verandert de sessie niet.

       Gevolg: een tweede kind verscheen wél in het toestemmingspaneel (dat
       leest rechtstreeks de server) en NIET in de lijst of de wisselaar. De
       ouder kreeg te horen dat het gelukt was en kon er niet naartoe. De
       oude tekst gaf dat half toe met "zodra het scherm ververst" — maar
       verversen hielp niet, want de projectie hing aan de sessie.

       Bewust hier en niet in de RPC-laag: dit is een projectie naar de
       clientstaat, en die hoort bij de handeling die hem veroorzaakt.
       */
      const profile = getActiveProfile();
      if (profile?.id) {
        await syncLinkedChildren(profile.id);
      }

      // De multi_parent-mededeling. Een ouder hoort te weten dat hij niet de
      // enige is die meekijkt — bij gescheiden ouders of een verzorger die
      // eerder koppelde is dat relevante informatie, en het is precies wat de
      // oude (dode) waarschuwing bedoelde te zeggen.
      const others = result.otherParentCount ?? 0;
      if (others > 0) {
        setNotice(others === 1 ? 'Er kijkt nog één andere ouder of verzorger mee met dit kind.' : `Er kijken nog ${others} andere ouders of verzorgers mee met dit kind.`);
      }
      setLinkCode('');
    } finally {
      setIsSubmitting(false);
    }
  };
  return {
    linkCode,
    setLinkCode,
    error,
    success,
    notice,
    isSubmitting,
    handleSubmit
  };
}